import React, { useEffect, useState,useRef ,useCallback} from "react"
import { Input, Button } from "components"
import loader from "./loading.gif"
import _ from "lodash";
import { useLocation } from "@reach/router";
import {parse} from "query-string";
import { navigate } from "gatsby"
import axios from "axios";
import { isValidZipCode } from "utils"

const CONTIGUOUS_CENTER_OF_US = "66952"; // Lebanon, Kansas
const INVALID_ZIP_CODE = "Please enter a valid zip code.";
const NO_RESULTS_FOUND = "No jobs match your search. Please try again.";
const INTERNAL_ERROR = "Something went wrong, please try again later.";
const ZIP_CODE_REQUIRED = "Zip Code is required.";
const MAX_US_RADIUS = 2000;

/**
 * React component CareerBoard .
 * Shows the joab board display fields and job listings.
 * @param  {array} newJobFeeds
 * @param  {string} newjobKeyWord
 * @param  {string} newJobZipCode
 * @param  {string} ApiErrorMessage
 * @param  {boolean} isLoading
 * @param  {function} getSelectedListing
 * @param  {function} searchJobs
 */
function CareerBoard() {
  const browserLocation = useLocation()
  let {
    zipcode: queryParamZipCode,
    keyword: queryParamKeyword,
    all: queryParamAll,
  } = parse(browserLocation.search)
  const [jobKeyWord, setJobKeyWord] = useState(queryParamKeyword ? queryParamKeyword : "")
  const [jobZipCode, setJobZipCode] = useState(queryParamZipCode ? queryParamZipCode : "")
  const [showAllJobs, setShowAllJobs] = useState(false)
  const [jobFeedList, setJobFeedList] = useState([])
  const [displayChunk, setDisplayChunk] = useState([])
  const [goSearchJobs, setGoSearchJobs] = useState(false)
  const [ApiErrorMessage, setApiErrorMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [scrollCounter, setScrollCounter] = useState(0)
  const [jobCount, setJobCount] = useState(0)

  // PROD
  const baseURL = "https://ekprmilgjh.execute-api.us-east-1.amazonaws.com/Prod/search?"
  // DEV
  // const baseURL = "https://6hrrdoabd5.execute-api.us-east-1.amazonaws.com/Prod/search?"

  const observer = useRef();

  /**
   * Side effect hook to make API call to get job feeds when user is coming from landing page.
   * Runs only when component mounts
   * @param  {}
   */
  useEffect(() => {
    if (!jobZipCode && !queryParamAll) {
      return
    }
    if (queryParamAll){
      setShowAllJobs(true)
    }
    setJobKeyWord(jobKeyWord)
    setJobZipCode(jobZipCode)
    if (queryParamAll) {
      getJobFeeds(jobKeyWord, jobZipCode, true)
    } else if (isValidZipCode(jobZipCode)) {
      getJobFeeds(jobKeyWord, jobZipCode)
    } else {
      setApiErrorMessage(INVALID_ZIP_CODE)
    }
  }, [])


  /**
   * Side effect hook to make API call to get job feeds when user is coming from landing page.
   * Runs only when component mounts
   * @param  {}
   */
  useEffect(() => {
    if (goSearchJobs) {
      if (jobZipCode || showAllJobs) {
        //TODO :  could cause a redundant render on mount
        if (showAllJobs) {
          getJobFeeds(jobKeyWord, jobZipCode, true)
        } else if (isValidZipCode(jobZipCode)) {
          getJobFeeds(jobKeyWord, jobZipCode)
        } else {
          setApiErrorMessage(INVALID_ZIP_CODE)
        }
        if (typeof window !== "undefined") {
          if (showAllJobs) {
            navigate("/careers/job-board?all=true", { replace: true})
          } else {
            navigate(
              `/careers/job-board?keyword=${jobKeyWord}&zipcode=${jobZipCode}`,
              { replace: true}
            )
          }
        }
      } else if (jobZipCode === "") {
        setApiErrorMessage(ZIP_CODE_REQUIRED)
      }
      setGoSearchJobs(false)
    }
  }, [goSearchJobs])
  /**
   * Callback fo call when refrence to last element changes 
   * Checks if it is the viewport and loads more 
   * Runs only when component mounts
   * @param  {} 
   */
  const lastJobElement = useCallback((node)=>{
   
    if(observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries=>{
        if(entries[0].isIntersecting){
          setScrollCounter(prevScrollCounter => prevScrollCounter+1)
        }
      })
    if(node) observer.current.observe(node)
  },[jobFeedList,displayChunk])

  
/**
   * split jobfeeds into 10 size arrays for loading in 10s
   * @param  {object} jobFeeds
   */
const splitJobFeeds = jobFeeds => {
  let chunks = _.chunk(jobFeeds, 10)
  return chunks
}

/**
   * setting selected listing after user clicks on job listing from job boards page
   * @param  {object} listing
   */
  const getSelectedListing = listing => {
    if (listing) {
      if (typeof window !== "undefined") {
        navigate(`/careers/job-details?id=${listing.hash}`)
      }
    }
  }

  /**
   * Side effect to set job feed list runs lik a component did update only 
   * when new job feeds are fed into the component. 
   * Runs only when component mounts
   * @param  {} 
   */
  useEffect(()=>{
    
    if(scrollCounter>0){
      if(scrollCounter < jobFeedList.length){
        setDisplayChunk([...displayChunk,...jobFeedList[scrollCounter]])
      }
    }
 },[scrollCounter])


 /**
   * function getJobFeeds makes axios api call to lambda API to get job feeds.
   * @param  {string} keyword
   * @param  {string} zipCode
   */
  const getJobFeeds = async (keyword, zipCode, searchAll = false) => {
    setApiErrorMessage("");
    setJobFeedList([])
    setIsLoading(true)
    try {
      const encodedKeyword = encodeURIComponent(keyword);
      const url = searchAll
        ? `${baseURL}zipCode=${CONTIGUOUS_CENTER_OF_US}&keywords=&distance=${MAX_US_RADIUS}`
        : `${baseURL}zipCode=${zipCode}&keywords=${encodedKeyword}`
      const response = await axios.get(url, {
        headers: { "content-type": "application/json" },
        validateStatus: function (status) {
          return status < 500;
        }
      })
      const { data } = response

      if (response.status === 200 && data.nearbyListings) {
        setIsLoading(false)
        if (data.nearbyListings.length > 0) {
          setJobCount(data.nearbyListings.length)
          const tempFeeds = splitJobFeeds(data.nearbyListings)
          setJobFeedList(tempFeeds)
          setDisplayChunk([...tempFeeds[0]])
        } else {
          setApiErrorMessage(NO_RESULTS_FOUND);
        }
      } else {
        setApiErrorMessage(data.message)
        setIsLoading(false)
      }
    } catch (err) {
      setApiErrorMessage(INTERNAL_ERROR)
      setIsLoading(false)
    }
  }

  /**
   * processes hiring rates to format displayed on UI
   * @param  {object} listing
   */
  const  formatHiringRate =(listing) => {
    if (!listing ||!listing.MinHiringRate||!listing.MaxHiringRate) {
      return null;
    }
    const minRate = listing.MinHiringRate.toFixed(2)
    const maxRate = listing.MaxHiringRate.toFixed(2)
    return <p className="body flex-1 shrink-0"><span>{`$${minRate} - $${maxRate}`}</span></p>
  }
  
  const searchJobs = (keyWord, zipCode) => {
    setJobKeyWord(keyWord)
    setJobZipCode(zipCode)
    setShowAllJobs(false)
    setGoSearchJobs(true)
  }

  const viewAllJobs = () => {
    setShowAllJobs(true);
    setJobKeyWord("")
    setJobZipCode("")
    setGoSearchJobs(true)
  }

  /**
   * returns search form to be 
   * displayed on the UI when component renders.
   */
  const displaySearchForm = () => {
    return (
      <div className="space-y-10">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <Input
              id="keywords"
              placeholder="Keywords"
              value={jobKeyWord}
              onChange={e => {
                setJobKeyWord(e.target.value)
              }}
            />
          </div>
          <div className="flex-1">
            <Input
              id="zipCode"
              placeholder="Zip Code (Required)"
              value={jobZipCode}
              onChange={e => {
                setJobZipCode(e.target.value)
              }}
            />
          </div>
        </div>
        <div className="flex gap-x-3 justify-center flex-col space-y-5 sm:flex-row sm:space-y-0">
          <Button
            label="Search Jobs"
            type="submit"
            appearance="primary"
            onClick={() => {
              setScrollCounter(0)
              setDisplayChunk([])
              searchJobs(jobKeyWord, jobZipCode)
            }}
            size="job-board"
          />
          <Button
            label="View All Jobs"
            type="submit"
            appearance="secondary"
            onClick={() => {
              setScrollCounter(0)
              setDisplayChunk([])
              viewAllJobs()
            }}
            size="job-board"
          />
        </div>
      </div>
    )
  }

  /**
   * Returns error message to be displayed
   * Can be a data error or an API server error
   * @param  {string} ApiErrorMessage
   */
  const showErrorMessage =(ApiErrorMessage) => {
    return (
      <div className="space-y-2">
        <div className="flex justify-center gap-6 mb-16">
          <div className="flex justify-center">
             <p className="nav-primary text-red">{ApiErrorMessage}</p>
          </div>
        </div>
      </div>
    )
  }

  /**
   * function returns loading spinner gif 
   * to be displayed when data is still loading from api call.
   */
  const displaySpinner =() => {
      if(isLoading){
        return (
          <div className="space-y-2">
            <div className="flex  justify-center gap-6 mb-16">
              <div className="flex justify-center">
              <img
                alt="spinner"
                src={loader}
              />
              </div>
            </div>
          </div>
        )
      }
  }

  

  /**
   * Function to display job feeds 
   * Calls diplay spinner when data is still being loading to display loading spinner
   * calls show error message in case of an error message
   * otherwise displays list of job listings
   */
  const displayJobFeeds =() => {
    if(isLoading){
      return displaySpinner()
    }else if (ApiErrorMessage && displayChunk.length === 0) {
       return showErrorMessage(ApiErrorMessage)
    }
    const jobFeedsArr = displayChunk.map((listing, i) => {
      if(displayChunk.length===i+1){
        return (
          <div className="job-list-item" ref={lastJobElement} key={`job-${i}`} onClick={()=>{getSelectedListing(listing)}}>
            <div className="flex flex-row justify-between md:justify-start">
              <div className="flex flex-col w-2/3">
                <p className="flex-1 shrink-0 lead-copy-no-min-height">
                  <span>{listing.Title}</span>
                </p>
                {formatHiringRate(listing)}
              </div>
              <div className="md:w-1/3">
                <p className="font-bold text-base">{`${listing.City}, ${listing.State}`}</p>
              </div>
            </div>
          </div>
        )
      }
      return (
        <div className="job-list-item" key={`job-${i}`} onClick={()=>{getSelectedListing(listing)}}>
          <div className="flex flex-row justify-between md:justify-start">
            <div className="flex flex-col w-2/3">
              <p className="flex-1 shrink-0 lead-copy-no-min-height">
                <span>{listing.Title}</span>
              </p>
              {formatHiringRate(listing)}
            </div>
            <div className="md:w-1/3">
              <p className="font-bold text-base">{`${listing.City}, ${listing.State}`}</p>
            </div>
          </div>
        </div>
      )
    })
    if (jobFeedsArr.length > 0) {
      jobFeedsArr.unshift(
        <div className="job-list-header" key="listings-header">
          <p className="nav-primary font-bold text-black text-opacity-25	mb-10">
            {`Showing ${jobCount} jobs`}
          </p>
          <div className="flex flex-row justify-between md:justify-start">
            <div className="md:w-2/3"><p className="lead-copy font-bold">Position</p></div>
            <div className="md:w-1/3"><p className="lead-copy font-bold">Location</p></div>
          </div>
        </div>
      )
    }
    return <div className="job-listing-grid">{jobFeedsArr}</div>
  }

  /*
     component  render return block
  */

  return (
    <section
      data-slice="CareerBoard"
      className="relative wrapper md:pt-20 md:pb-0"
    >
      <div className="default-grid gap-y-5">
        <h2 className="text-center h1 text-darkPurple md:col-start-2 md:col-span-10 col-span-full">
          Hiring tomorrow’s leaders today
        </h2>
      </div>
      <div className="default-grid">
        <div className="px-4 py-5 col-span-full col-start-1  md:px-10 md:py-8 md:col-start-3 md:col-span-8">
          { displaySearchForm() }
        </div>
      </div>
      <div>{displayJobFeeds()}</div>
    </section>
  )
  
}

export default CareerBoard


